import React, { useState, useEffect, useCallback, useRef } from 'react';
import { LandingPageIframe } from './LandingPageIframe.tsx';
import { fetchPlayerBalances, fetchAggregationProgress, type IWidgetConfig, type RawBalanceItem, type AggregationProgressResponse } from '../api/apiClient.ts';
import { Token, Ott } from '../api/apiClient.ts';
import { useSignalRConnection } from '../api/signalR.ts';
import { eventDispacher } from '../api/eventDispatcher.ts';
import { ProgressUpdateEvent, BalanceUpdateEvent } from '../api/baseEventType.ts';
import type { MessageData } from '../types/signaler.events.types.ts';
// @ts-ignore
import CircleProgress from './spiral/CircleProgress.jsx';

const WIDGET_POSITION_STORAGE_KEY = 'balance-widget-position';

interface WidgetProps {
  config: IWidgetConfig;
  configurationId?: string;
  lang?: string;
  /**
   * 'cumulative' (default): denominator keeps growing across steps (0/5 -> 4/5 -> 9/10).
   * 'step': denominator stays fixed at the step size, numerator resets each step (0/5 -> 4/5 -> 4/5).
   */
  progressMode?: 'cumulative' | 'step';
}

export const Widget: React.FC<WidgetProps> = ({ config, lang = 'en', progressMode = 'cumulative' }) => {
  const [progressPercentage, setProgressPercentage] = useState<number>(0);
  const [isLoading, setIsLoading] = useState(true);
  const [showIframe, setShowIframe] = useState(false);
  const [displayedBalance, setDisplayedBalance] = useState<RawBalanceItem>({ amount: 0 });
  const latestBalance = useRef<RawBalanceItem>({ amount: 0 });
  const [currentProgress, setCurrentProgress] = useState<number>(0);
  const [aggregationProgress, setAggregationProgress] = useState<AggregationProgressResponse | null>(null);
  const [displayCurrent, setDisplayCurrent] = useState<number | null>(null);
  const [displayNext, setDisplayNext] = useState<number | null>(null);
  const circleRef = useRef<{ updateProgress: (p: number) => Promise<void> } | null>(null);
  const [isMobile, setIsMobile] = useState(() => window.matchMedia('(max-width: 480px)').matches);

  // Dragging the widget button around the viewport. Position is tracked as
  // distance from the right/bottom edges (not left/top) so the widget stays
  // anchored to the bottom-right corner on resize, same as its default CSS
  // position — dragging near a corner keeps it pinned to that corner instead
  // of drifting away as the viewport grows or shrinks.
  const widgetRef = useRef<HTMLDivElement | null>(null);
  const [dragPos, setDragPos] = useState<{ right: number; bottom: number } | null>(() => {
    try {
      const saved = localStorage.getItem(WIDGET_POSITION_STORAGE_KEY);
      if (!saved) return null;
      const parsed = JSON.parse(saved);
      return typeof parsed?.right === 'number' && typeof parsed?.bottom === 'number'
        ? parsed
        : null;
    } catch {
      return null;
    }
  });
  const [isDragging, setIsDragging] = useState(false);
  const dragStateRef = useRef<{ startX: number; startY: number; originRight: number; originBottom: number; moved: boolean } | null>(null);
  const suppressClickRef = useRef(false);
  const dragPosRef = useRef(dragPos);
  useEffect(() => {
    dragPosRef.current = dragPos;
  }, [dragPos]);

  useEffect(() => {
    const mql = window.matchMedia('(max-width: 480px)');
    const handler = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', handler);
    return () => mql.removeEventListener('change', handler);
  }, []);

  const displaySize = isMobile ? 52 : 70;
  const circleSize = displaySize * 2;
  const strokeWidth = (isMobile ? 6 : 8) * 2;

  // Message handler for SignalR
  const handleShowMessage = useCallback((messageData: MessageData) => {
    console.log('📨 SignalR message received:', {
      message: messageData.message,
      type: messageData.type,
      contentType: messageData.contentType,
      localizedMessages: messageData.localizedMessages,
      fullData: messageData
    });
  }, []);

  // Set up SignalR connection
  const { isConnected } = useSignalRConnection(
    config.promotionId,
    Token,
    handleShowMessage
  );

  // Log connection status changes
  useEffect(() => {
    if (isConnected) {
      console.log('✅ SignalR connected successfully', {
        promotionId: config.promotionId,
        coinId: config.coinId,
        triggerId: config.triggerId
      });
    } else {
      console.log('❌ SignalR disconnected or not connected', {
        promotionId: config.promotionId
      });
    }
  }, [isConnected, config.promotionId, config.coinId, config.triggerId]);

  const fetchBalance = useCallback(async () => {
    try {
      if (!Token) return
      const data = await fetchPlayerBalances(config.coinId)
      console.log('Fetched balance data:', data)
      latestBalance.current = data;
      setDisplayedBalance(data); // show immediately on initial load
    } catch (err) {
      console.error('Failed to fetch balance:', err)
    } finally {
      setIsLoading(false);
    }
  }, [config.coinId])

  const updateProgressFromData = useCallback((data: AggregationProgressResponse) => {
    setAggregationProgress(data);

    const rule = data.awardRule;
    if (!rule?.linear) return;

    const every = Number(rule.linear.every) || 0;
    const give = Number(rule.linear.give) || 0;
    const limit = rule.linear.limit != null && rule.linear.limit !== '' ? Number(rule.linear.limit) : null;
    if (every <= 0) return;

    const anchor = Number(data.anchorMemVar ?? 0);
    const currentValue = Number(data.currentValue ?? 0);
    const scoreSinceAnchor = Math.max(0, currentValue - anchor);
    const rawUnits = Math.floor(scoreSinceAnchor / every);

    const fullMaxUnits = limit != null && give > 0 ? Math.floor(limit / give) : null;
    const hasRemainder = fullMaxUnits != null && fullMaxUnits * give < limit! ? 1 : 0;
    const maxUnits = fullMaxUnits != null ? fullMaxUnits + hasRemainder : null;
    const unitsCompleted = maxUnits != null ? Math.min(rawUnits, maxUnits) : rawUnits;

    const earned = data.awardedSoFar ?? 0;
    const capped = (maxUnits != null && rawUnits >= maxUnits) || (limit != null && earned >= limit);

    const progressInUnit = capped ? every : scoreSinceAnchor - unitsCompleted * every;
    const percentage = (progressInUnit / every) * 100;

    if (progressMode === 'step') {
      // Fixed denominator: numerator is the remainder within the current step.
      setDisplayCurrent(progressInUnit);
      setDisplayNext(every);
    } else if (capped && limit != null && fullMaxUnits != null) {
      const maxThreshold = (fullMaxUnits + hasRemainder) * every;
      setDisplayCurrent(maxThreshold);
      setDisplayNext(maxThreshold);
    } else {
      setDisplayCurrent(currentValue);
      setDisplayNext(anchor + (unitsCompleted + 1) * every);
    }

    setCurrentProgress(progressInUnit);
    setProgressPercentage(Math.min(100, percentage));
  }, [progressMode]);

  const fetchProgress = useCallback(async () => {
    if (!config.triggerId) return;

    try {
      const data = await fetchAggregationProgress(config.triggerId);
      console.log('Fetched aggregation progress:', data);
      updateProgressFromData(data);
    } catch (err) {
      console.error('Failed to fetch aggregation progress:', err);
    }
  }, [config.triggerId, updateProgressFromData]);

  useEffect(() => {
    fetchBalance()
    fetchProgress()
  }, [fetchBalance, fetchProgress, config])

  // Subscribe to ProgressUpdateEvent
  useEffect(() => {
    const handleProgressUpdateEvent = (event: ProgressUpdateEvent) => {
      if (event.AggregationId === config.triggerId && aggregationProgress) {
        console.log('Progress update received:', event);
        updateProgressFromData({
          ...aggregationProgress,
          currentValue: event.CurrentScore,
        });
      }
    };

    eventDispacher.on(ProgressUpdateEvent, handleProgressUpdateEvent);

    return () => {
      eventDispacher.off(ProgressUpdateEvent, handleProgressUpdateEvent);
    };
  }, [config.triggerId, aggregationProgress, updateProgressFromData]);

  // Subscribe to BalanceUpdateEvent
  useEffect(() => {
    const handleBalanceUpdateEvent = (event: BalanceUpdateEvent) => {
      if (event.CoinId === config.coinId) {
        console.log('Balance update received:', event);
        const updated = { ...latestBalance.current, amount: event.Amount };
        latestBalance.current = updated;
        setDisplayedBalance(updated);
      }
    };

    eventDispacher.on(BalanceUpdateEvent, handleBalanceUpdateEvent);

    return () => {
      eventDispacher.off(BalanceUpdateEvent, handleBalanceUpdateEvent);
    };
  }, [config.coinId]);

  // Drive CircleProgress when progressPercentage changes; update displayed balance after animation
  useEffect(() => {
    if (!circleRef.current) return;
    circleRef.current.updateProgress(progressPercentage).then(() => {
      setDisplayedBalance({ ...latestBalance.current });
    });
  }, [progressPercentage, displayNext]);

  const clampToViewport = useCallback((right: number, bottom: number) => {
    const rect = widgetRef.current?.getBoundingClientRect();
    // `|| ` (not `??`) on purpose: while the iframe is open the widget is
    // hidden via `display: none`, so its rect reports 0x0 — fall back to
    // displaySize in that case too, same as when the ref isn't set at all.
    const width = rect?.width || displaySize;
    const height = rect?.height || displaySize;
    return {
      right: Math.min(Math.max(right, 0), Math.max(0, window.innerWidth - width)),
      bottom: Math.min(Math.max(bottom, 0), Math.max(0, window.innerHeight - height)),
    };
  }, [displaySize]);

  const handlePointerMove = useCallback((e: PointerEvent) => {
    const drag = dragStateRef.current;
    if (!drag) return;

    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (Math.abs(dx) > 4 || Math.abs(dy) > 4) drag.moved = true;

    // Moving the pointer right/down shrinks the distance to the right/bottom edges.
    setDragPos(clampToViewport(drag.originRight - dx, drag.originBottom - dy));
  }, [clampToViewport]);

  const handlePointerUp = useCallback(() => {
    window.removeEventListener('pointermove', handlePointerMove);
    window.removeEventListener('pointerup', handlePointerUp);
    if (dragStateRef.current?.moved) {
      suppressClickRef.current = true;
      if (dragPosRef.current) {
        try {
          localStorage.setItem(WIDGET_POSITION_STORAGE_KEY, JSON.stringify(dragPosRef.current));
        } catch {
          // ignore storage errors (e.g. quota, disabled storage)
        }
      }
    }
    dragStateRef.current = null;
    setIsDragging(false);
  }, [handlePointerMove]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    const rect = widgetRef.current?.getBoundingClientRect();
    if (!rect) return;

    dragStateRef.current = {
      startX: e.clientX,
      startY: e.clientY,
      originRight: window.innerWidth - rect.right,
      originBottom: window.innerHeight - rect.bottom,
      moved: false,
    };
    setIsDragging(true);
    window.addEventListener('pointermove', handlePointerMove);
    window.addEventListener('pointerup', handlePointerUp);
  };

  // Re-clamp the restored position once the widget is mounted and measurable
  useEffect(() => {
    setDragPos((pos) => (pos ? clampToViewport(pos.right, pos.bottom) : pos));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Keep the widget anchored to the bottom-right corner if the viewport resizes
  useEffect(() => {
    if (!dragPos) return;
    const handleResize = () => setDragPos((pos) => (pos ? clampToViewport(pos.right, pos.bottom) : pos));
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [dragPos, clampToViewport]);

  const handleWidgetClick = () => {
    if (suppressClickRef.current) {
      suppressClickRef.current = false;
      return;
    }
    setShowIframe(true);
  };

  const handleCloseIframe = () => {
    setShowIframe(false);
  };
  // Kept mounted (just visually hidden) while the iframe is open, instead of
  // unmounting — CircleProgress holds its animated-progress state internally,
  // so unmounting/remounting it would reset the ring to 0% on every close.
  return (
    <>
      <div
        ref={widgetRef}
        className="balance-widget"
        onClick={handleWidgetClick}
        onPointerDown={handlePointerDown}
        style={{
          ...(dragPos
            ? {
                right: dragPos.right,
                bottom: dragPos.bottom,
                left: 'auto',
                top: 'auto',
                transition: isDragging ? 'none' : undefined,
                touchAction: 'none',
              }
            : { touchAction: 'none' }),
          display: showIframe ? 'none' : undefined,
        }}
      >
        <div style={{ width: displaySize, height: displaySize, overflow: 'hidden' }}>
          <CircleProgress ref={circleRef} size={circleSize} strokeWidth={strokeWidth}>
            <div className="balance-widget-coin">
              <div className="balance-widget-amount">{displayedBalance.amount}</div>
              {displayedBalance.coinUrl && (
                <div className="balance-widget-icon">
                  <img src={displayedBalance.coinUrl} alt="Coin" style={{ maxHeight: '24px' }} />
                </div>
              )}
            </div>
          </CircleProgress>
        </div>

        {displayCurrent !== null && displayNext !== null && (
          <div className="progress__point">
            <span className="progress__point--text">
              {Math.min(displayCurrent, displayNext)}/{displayNext}
            </span>
          </div>
        )}
      </div>

      {showIframe && (() => {
        const pageUrl = typeof config.pageUrl === 'string' ? config.pageUrl.trim() : '';
        if (!pageUrl) {
          console.error('Missing config.pageUrl for iframe');
          return null;
        }

        try {
          const url = pageUrl.startsWith('http') || pageUrl.includes('://')
            ? new URL(pageUrl)
            : new URL(pageUrl, window.location.origin);
          url.searchParams.set('ott', Ott);
          url.searchParams.set('lang', lang);
          // Fullscreen dialog: let the landing scroll itself (html.embedded-inner)
          // instead of the auto-height contract, which expects the host to grow
          // the iframe and kills the landing's internal scroll (overflow: hidden).
          url.searchParams.set('embedMode', 'inner');
          return (
            <LandingPageIframe
              url={url.toString()}
              onClose={handleCloseIframe}
            />
          );
        } catch (error) {
          console.error('Invalid config.pageUrl for iframe:', pageUrl, error);
          return null;
        }
      })()}
    </>
  );
};
